import type { GameState, Passenger } from '@full-elevator/sim';
import { MILLI_PER_FLOOR, nextStops } from '@full-elevator/sim';

/**
 * The guided first shifts (ADR-0030).
 *
 * Friends of the owner said the game needs a better tutorial, and the beta 2
 * recording showed why: on level 1 two of five newcomers did not tap the floor
 * on the ticket, and one closed the page. So the first three shifts teach one
 * idea each, by doing it:
 *
 *   1 — the ticket is the floor they want; tap it to take them there
 *   2 — several tickets, several stops: nearest first
 *   3 — the lift is full, the rest wait; go back for them
 *
 * and the trial of a second lift (ADR-0034) says, once, how a tap picks a car.
 *
 * A lesson stops the clock, lights what it is about and dims the rest. When
 * there is something to tap, a hand shows it and only that floor is taken
 * until it has been tapped. Each lesson is shown once per save.
 *
 * This file only reads the state, to find the moment a lesson is about. It
 * decides nothing about what the lift may do: the tap a lesson asks for is an
 * ordinary `callTo`, sent the way any tap is.
 */

/** Something a lesson lights while everything else dims. */
export type Spot =
  /** The people waiting on a floor, and the tickets they hold. */
  | { readonly kind: 'waiting'; readonly floor: number }
  /** The car, its riders' tickets and its count of seats; in a bank, which car (ADR-0034). */
  | { readonly kind: 'cabin'; readonly car?: number }
  /** A floor's whole row: the thing to tap. */
  | { readonly kind: 'floor'; readonly floor: number };

export interface CoachStep {
  /** Remembered in the save once done, so it is never shown again. */
  readonly id: string;
  /** A few short lines: two, or three where the lesson also says when. */
  readonly lines: readonly string[];
  readonly spots: readonly Spot[];
  /** The floor to tap to go on — a hand shows it — or null: tap anywhere. */
  readonly tap: number | null;
}

/** Floor names as the player reads them (ADR-0028). */
export type FloorNamer = (floor: number) => string;

interface Lesson {
  readonly id: string;
  /** Whether this is the moment the lesson is about. */
  readonly when: (state: GameState) => boolean;
  /** `tapAhead`: taps go in as stops in order (ADR-0052), which changes what "when" means. */
  readonly step: (state: GameState, name: FloorNamer, tapAhead: boolean) => Omit<CoachStep, 'id'>;
}

const waiting = (state: GameState): Passenger[] =>
  state.passengers.filter((p) => p.state === 'waiting');

const riders = (state: GameState): Passenger[] =>
  state.passengers.filter((p) => p.state === 'riding');

/** The floor the car is at, if it is standing at one. */
function carFloor(state: GameState): number | null {
  const { positionMilli, phase } = state.elevator;
  if (phase === 'moving' || positionMilli % MILLI_PER_FLOOR !== 0) return null;
  return positionMilli / MILLI_PER_FLOOR;
}

/** Standing still with nowhere to go: waiting for the player. */
const awaitingOrders = (state: GameState): boolean =>
  state.elevator.targetFloor === null && carFloor(state) !== null;

/** The riders' stops, nearest the car first (the lower on a tie). */
function stopsByDistance(state: GameState): number[] {
  const here = state.elevator.positionMilli / MILLI_PER_FLOOR;
  const stops = [...new Set(riders(state).flatMap((p) => nextStops(state, p)))];
  return stops.sort((a, b) => Math.abs(a - here) - Math.abs(b - here) || a - b);
}

const LESSONS: Readonly<Record<string, readonly Lesson[]>> = {
  '01-first-ride': [
    {
      id: 'first-ride/tickets',
      // Before anyone has moved: the people, and what they hold.
      when: (s) => s.elapsedMs === 0 && waiting(s).length > 0,
      step: (s, name) => {
        const floor = waiting(s)[0]?.standingFloor ?? 0;
        const wants = waiting(s)[0]?.destination ?? 0;
        return {
          lines: [
            'People wait for the lift on their floor.',
            `The number they hold is where they want to go: floor ${name(wants)}.`,
          ],
          spots: [{ kind: 'waiting', floor }],
          tap: null,
        };
      },
    },
    {
      id: 'first-ride/send',
      // Everyone aboard, and the lift waiting to be told where.
      when: (s) => waiting(s).length === 0 && riders(s).length > 0 && awaitingOrders(s),
      step: (s, name) => {
        const stop = stopsByDistance(s)[0] ?? 0;
        return {
          lines: ['They are aboard.', `Tap floor ${name(stop)} to take them there.`],
          spots: [{ kind: 'cabin' }, { kind: 'floor', floor: stop }],
          tap: stop,
        };
      },
    },
  ],
  '02-three-stops': [
    {
      id: 'three-stops/nearest',
      when: (s) =>
        waiting(s).length === 0 &&
        stopsByDistance(s).length > 1 &&
        s.score.delivered === 0 &&
        awaitingOrders(s),
      step: (s, name, tapAhead) => {
        const stops = stopsByDistance(s);
        const first = stops[0] ?? 0;
        // Name every stop. "Carry on the same way" read as "one floor at a
        // time": two of five fresh players then tapped a floor no ticket showed
        // (studio review, 2026-10-22).
        const then = stops
          .slice(1)
          .map((f) => `then ${name(f)}`)
          .join(', ');
        return {
          lines: [
            `${stops.length} tickets: ${stops.map(name).join(', ')}.`,
            `Nearest first: tap ${name(first)}, ${then}. Only the floors on their tickets.`,
            // When, too: a tap sets where the lift goes, it does not queue, so a
            // tap ahead runs the car past the floor before (qa-lead review,
            // problem 2; ADR-0047 keeps "the last tap wins").
            tapAhead
              ? 'Tapping ahead: tap the others now too, in that order, and the lift goes to each in turn.'
              : 'The lift goes to the last floor you tap: tap the next one once the doors open.',
          ],
          spots: [{ kind: 'cabin' }, { kind: 'floor', floor: first }],
          tap: first,
        };
      },
    },
  ],
  '03-lobby-rush': [
    {
      id: 'lobby-rush/full',
      // Full, with people still on the landing it is standing at.
      when: (s) => {
        const here = carFloor(s);
        return (
          here !== null &&
          s.elevator.load >= s.spec.capacity &&
          waiting(s).some((p) => p.standingFloor === here)
        );
      },
      step: (s) => {
        const here = carFloor(s) ?? 0;
        const left = waiting(s).filter((p) => p.standingFloor === here).length;
        return {
          lines: [
            `The lift holds ${s.spec.capacity}, and it is full.`,
            left === 1
              ? 'The last one waits for the next trip.'
              : `The other ${left} wait for the next trip.`,
          ],
          spots: [{ kind: 'cabin' }, { kind: 'waiting', floor: here }],
          tap: null,
        };
      },
    },
    {
      id: 'lobby-rush/back',
      // Empty again, away from the people still waiting.
      when: (s) => {
        const here = carFloor(s);
        return (
          s.elevator.load === 0 &&
          awaitingOrders(s) &&
          waiting(s).some((p) => p.standingFloor !== here)
        );
      },
      step: (s, name) => {
        const here = carFloor(s);
        const floor = waiting(s).find((p) => p.standingFloor !== here)?.standingFloor ?? 0;
        return {
          lines: ['Empty again.', `Tap ${name(floor)} to go back for them.`],
          spots: [
            { kind: 'waiting', floor },
            { kind: 'floor', floor },
          ],
          tap: floor,
        };
      },
    },
  ],
  // The trial of a second lift (ADR-0034): what is new is only how a tap picks a car.
  '51-the-second-car': [
    {
      id: 'second-car/choose',
      when: (s) => s.elapsedMs === 0 && s.extraCars.length > 0,
      step: (_s, _name, tapAhead) => ({
        lines: [
          'Two lifts this shift, both yours.',
          tapAhead
            ? 'Tap a lift to choose it: the floors you tap next are its stops, numbered in its shaft.'
            : 'Tap a lift to choose it: the floor you tap next is where it goes.',
        ],
        spots: [
          { kind: 'cabin', car: 0 },
          { kind: 'cabin', car: 1 },
        ],
        tap: null,
      }),
    },
    {
      id: 'second-car/shaft',
      when: (s) => s.elapsedMs === 0 && s.extraCars.length > 0,
      step: () => ({
        lines: ['Or tap a floor in a lift’s own shaft:', 'that lift goes straight there.'],
        spots: [
          { kind: 'cabin', car: 0 },
          { kind: 'cabin', car: 1 },
        ],
        tap: null,
      }),
    },
  ],
};

/** The lesson this moment is about, if one is due and not yet done. */
export function lessonFor(
  levelId: string,
  state: GameState,
  done: readonly string[],
  name: FloorNamer,
  tapAhead = false,
): CoachStep | null {
  if (state.status !== 'playing') return null;
  for (const lesson of LESSONS[levelId] ?? []) {
    if (done.includes(lesson.id) || !lesson.when(state)) continue;
    return { id: lesson.id, ...lesson.step(state, name, tapAhead) };
  }
  return null;
}

/** Every lesson a level teaches, by id. */
export function lessonIds(levelId: string): string[] {
  return (LESSONS[levelId] ?? []).map((lesson) => lesson.id);
}

/** The levels that teach. */
export const COACHED_LEVELS: readonly string[] = Object.keys(LESSONS);
